import React, { useRef, useEffect, useState, useMemo } from "react";
import axios from "axios";
import { config } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import Confetti from "react-confetti";
import { ScratchCardApi, ScratchCardConfig } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";

// Public-only scratch card layout: left scratch card, right verification
export function PublicScratchCardContent() {
  const { toast } = useToast();
  const [mobile, setMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isScratching, setIsScratching] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [wonPoints, setWonPoints] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiSize, setConfettiSize] = useState({ width: 0, height: 0 });
  const [isCollected, setIsCollected] = useState(false);
  const [isCollecting, setIsCollecting] = useState(false);
  const [scratchCardConfig, setScratchCardConfig] = useState<ScratchCardConfig | null>(null);
  const selectedScratchCard = scratchCardConfig;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const scratchedAreaRef = useRef(0);
  const totalAreaRef = useRef(0);
  const rewardRef = useRef<number | null>(null);

  useEffect(() => {
    const updateSize = () => {
      if (typeof window !== "undefined") {
        setConfettiSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealed || !selectedScratchCard || !isOtpVerified) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const width = rect.width || 400;
    const height = rect.height || 448;

    canvas.width = width;
    canvas.height = height;

    ctx.fillStyle = "#9ca3af";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 32px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Scratch Here", canvas.width / 2, canvas.height / 2);

    totalAreaRef.current = canvas.width * canvas.height;
    scratchedAreaRef.current = 0;

    if (rewardRef.current === null && selectedScratchCard) {
      const randomValue = Math.random();
      if (randomValue < 0.333) {
        rewardRef.current = selectedScratchCard.smallReward;
      } else if (randomValue < 0.666) {
        rewardRef.current = selectedScratchCard.mediumReward;
      } else {
        rewardRef.current = selectedScratchCard.bigReward;
      }
    } else if (rewardRef.current === null) {
      rewardRef.current = 0;
    }
  };

  // Initialize scratch layer only after OTP is verified and config is available
  useEffect(() => {
    if (canvasRef.current && !isRevealed && selectedScratchCard && isOtpVerified) {
      // Reset scratch state whenever OTP just got verified
      scratchedAreaRef.current = 0;
      rewardRef.current = rewardRef.current ?? null;
      const timer = setTimeout(() => initializeCanvas(), 100);
      requestAnimationFrame(() => initializeCanvas());
      return () => clearTimeout(timer);
    }
  }, [isRevealed, selectedScratchCard, isOtpVerified]);

  useEffect(() => {
    const handleResize = () => {
      if (!isRevealed) {
        initializeCanvas();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isRevealed]);

  useEffect(() => {
    const fetchByClientId = async () => {
      try {
        const response = await axios.post(
          `${config.GIFT_API_BASE_URL}/ScratchCardConfig/GetScratchCardConfigByClientId`,
          { p_ClientId: 1009 },
          {
            headers: {
              xapikey: config.GIFT_API_KEY,
              "Content-Type": "application/json",
            },
          }
        );
        const data = response.data;
        if (data.error_msg && data.error_msg.trim() !== "") {
          toast({
            title: "Error",
            description: data.error_msg,
            variant: "destructive",
          });
        } else if (Array.isArray(data.clientScratchCard) && data.clientScratchCard.length > 0) {
          setScratchCardConfig(data.clientScratchCard[0]);
          // If OTP already verified by the time config arrives, reset and init overlay
          if (isOtpVerified) {
            setIsRevealed(false);
            setWonPoints(null);
            setIsCollected(false);
            scratchedAreaRef.current = 0;
            rewardRef.current = null;
          }
        } else {
          setScratchCardConfig(null);
        }
      } catch (error: any) {
        console.error("Error fetching scratch card config by client id:", error);
        const errorMessage = HandleError(error);
        toast({
          title: "Error",
          description: errorMessage || "Failed to fetch scratch card configuration",
          variant: "destructive",
        });
      }
    };
    fetchByClientId();
  }, [toast, isOtpVerified]);

  const handleSendOtp = () => {
    if (!mobile || mobile.length < 7 || mobile.length > 15) {
      toast({
        title: "Invalid mobile number",
        description: "Please enter a valid mobile number (7-15 digits).",
        variant: "destructive",
      });
      return;
    }
    setOtpSent(true);
    setIsOtpVerified(false);
    setOtp("");
    toast({
      title: "OTP sent",
      description: "A test OTP has been 'sent' to the entered mobile number.",
    });
  };

  const handleVerifyOtp = () => {
    if (!otp || otp.length < 4) {
      toast({
        title: "Invalid OTP",
        description: "Please enter a valid OTP.",
        variant: "destructive",
      });
      return;
    }

    if (otp !== "1992") {
      toast({
        title: "Incorrect OTP",
        description: "Please enter the correct OTP.",
        variant: "destructive",
      });
      setIsOtpVerified(false);
      return;
    }

    // On successful verification, ensure scratch state is reset and overlay is prepared
    setIsRevealed(false);
    setWonPoints(null);
    setIsCollected(false);
    scratchedAreaRef.current = 0;
    rewardRef.current = null;
    setIsOtpVerified(true);
    toast({
      title: "OTP verified",
      description: "You can now scratch the card.",
    });
  };

  const getEventCoordinates = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    if ("touches" in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    } else {
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  const scratchAt = (x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealed || !isOtpVerified || !selectedScratchCard) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (canvas.width === 0 || canvas.height === 0) {
      initializeCanvas();
      return;
    }

    const radius = 30;
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparentPixels = 0;
    const data = imageData.data;
    for (let i = 3; i < data.length; i += 40) {
      if (data[i] === 0) {
        transparentPixels += 10;
      }
    }
    scratchedAreaRef.current = transparentPixels;

    const scratchedPercentage =
      (scratchedAreaRef.current / totalAreaRef.current) * 100;
    if (scratchedPercentage >= 30 && !isRevealed) {
      revealReward();
    }
  };

  const revealReward = () => {
    setIsRevealed(true);
    setWonPoints(rewardRef.current);
    if (rewardRef.current && rewardRef.current > 0) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 8000);
    }
    toast({
      title: "Reward Revealed!",
      description: `You won ${rewardRef.current} points!`,
    });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (isRevealed || !isOtpVerified || !selectedScratchCard) return;
    if (e.button === 0) {
      isDrawingRef.current = true;
      setIsScratching(true);
      const coords = getEventCoordinates(e);
      if (coords) {
        scratchAt(coords.x, coords.y);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || isRevealed || !isOtpVerified || !selectedScratchCard) return;
    e.preventDefault();
    const coords = getEventCoordinates(e);
    if (coords) {
      scratchAt(coords.x, coords.y);
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    isDrawingRef.current = false;
    setIsScratching(false);
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      isDrawingRef.current = false;
      setIsScratching(false);
    };

    if (!isRevealed) {
      window.addEventListener("mouseup", handleGlobalMouseUp);
      return () => {
        window.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isRevealed]);

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (isRevealed || !isOtpVerified || !selectedScratchCard) return;
    e.preventDefault();
    isDrawingRef.current = true;
    setIsScratching(true);
    const coords = getEventCoordinates(e);
    if (coords) scratchAt(coords.x, coords.y);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || isRevealed || !isOtpVerified || !selectedScratchCard) return;
    e.preventDefault();
    const coords = getEventCoordinates(e);
    if (coords) scratchAt(coords.x, coords.y);
  };

  const handleTouchEnd = () => {
    isDrawingRef.current = false;
    setIsScratching(false);
  };

  const handleCollectReward = async () => {
    if (wonPoints === null || !selectedScratchCard) {
      return;
    }

    if (!mobile || !isOtpVerified) {
      toast({
        title: "Verification required",
        description: "Please verify your mobile number and OTP before collecting reward.",
        variant: "destructive",
      });
      return;
    }

    setIsCollecting(true);
    try {
      const payload = {
        p_MobileNo: mobile,
        p_OTP: "1992",
        p_PointsORDiscountWon: wonPoints,
        p_ScratchCardId: selectedScratchCard.id,
      };

      const response = await ScratchCardApi.insertScratchCardTransaction(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        setIsCollected(true);
        toast({
          title: "Reward Collected!",
          description: response.message || `${wonPoints} points have been added to your account.`,
        });
        setIsOtpVerified(false);
        setOtpSent(false);
        setOtp("");
        setMobile("");
        setWonPoints(null);
        setIsRevealed(false);
        rewardRef.current = null;
        scratchedAreaRef.current = 0;
      }
    } catch (error: any) {
      console.error("Error collecting reward:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to collect reward.",
        variant: "destructive",
      });
    } finally {
      setIsCollecting(false);
    }
  };

  const handleReset = () => {
    setIsRevealed(false);
    setWonPoints(null);
    setIsCollected(false);
    scratchedAreaRef.current = 0;
    rewardRef.current = null;
  };

  return (
    <div className="space-y-6 relative">
      {showConfetti && confettiSize.width > 0 && confettiSize.height > 0 && (
        <Confetti
          width={confettiSize.width}
          height={confettiSize.height}
          numberOfPieces={300}
          recycle={false}
        />
      )}

      <div className="grid gap-6 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] items-start">
        {/* Scratch Card Section (left) */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-full">
            <div className="relative rounded-lg overflow-hidden border-2 border-gray-300 bg-gradient-to-br from-blue-50 to-purple-50">
              {/* Show messages when not ready to scratch */}
              {!isRevealed && !isOtpVerified && (
                <div className="w-full h-[28rem] flex items-center justify-center bg-gray-100">
                  <p className="text-sm text-gray-500 text-center px-4">
                    Please verify your mobile number and OTP to scratch the card
                  </p>
                </div>
              )}
              {!isRevealed && isOtpVerified && !selectedScratchCard && (
                <div className="w-full h-[28rem] flex items-center justify-center bg-gray-100">
                  <p className="text-sm text-gray-500 text-center px-4">
                    Scratch card configuration is not available. Please try again later.
                  </p>
                </div>
              )}

              {/* Reward Display (hidden behind scratch layer) - only show when ready to scratch */}
              {!isRevealed && isOtpVerified && selectedScratchCard && (
                <>
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-gradient-to-br from-yellow-50 to-orange-50">
                    <div className="text-center space-y-2">
                      <p className="text-sm text-gray-600 font-medium">
                        You Won!
                      </p>
                      <p className="text-4xl font-bold text-emerald-600">
                        ?
                      </p>
                    </div>
                  </div>

                  {/* Scratchable Canvas Overlay */}
                  <canvas
                    ref={canvasRef}
                    className="w-full h-[28rem] cursor-crosshair touch-none relative z-10"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => {
                      isDrawingRef.current = false;
                      setIsScratching(false);
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    style={{
                      display: "block",
                      userSelect: "none",
                      pointerEvents: "auto",
                    }}
                  />
                </>
              )}

              {/* Revealed State */}
              {isRevealed && (
                <div className="w-full h-[28rem] flex flex-col items-center justify-center p-8 bg-gradient-to-br from-yellow-50 to-orange-50">
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-600 font-medium">
                      You Won!
                    </p>
                    <p className="text-4xl font-bold text-emerald-600">
                      {wonPoints} Points
                    </p>
                  </div>
                </div>
              )}
            </div>
            {!isRevealed && isOtpVerified && selectedScratchCard && (
              <p className="text-center text-sm text-gray-500 mt-2">
                Scratch to Reveal Your Reward!
              </p>
            )}
          </div>
        </div>

        {/* Mobile & OTP Section (right) */}
        <Card>
          <CardHeader>
            <CardTitle>Mobile Verification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Mobile Number</label>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="Enter mobile number"
                  value={mobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setMobile(value);
                  }}
                  maxLength={15}
                />
                <Button type="button" variant="outline" onClick={handleSendOtp}>
                  Send OTP
                </Button>
              </div>
            </div>

            {otpSent && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Enter OTP</label>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setOtp(value);
                    }}
                    maxLength={6}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleVerifyOtp}
                  >
                    Verify
                  </Button>
                </div>
                {isOtpVerified && (
                  <p className="text-xs text-emerald-600 font-medium">
                    OTP verified. You can scratch the card now.
                  </p>
                )}
              </div>
            )}

            {/* No selection dropdown: config is auto-loaded by client id */}

            {/* Collect Reward Section */}
            {isRevealed && !isCollected && (
              <div className="space-y-2">
                <Button
                  onClick={handleCollectReward}
                  className="w-full"
                  size="lg"
                  disabled={isCollecting || !selectedScratchCard}
                >
                  {isCollecting ? "Collecting..." : "Collect Reward"}
                </Button>
              </div>
            )}

            {isRevealed && isCollected && (
              <div className="space-y-2">
                <p className="text-center text-sm text-emerald-600 font-medium">
                  Reward collected successfully!
                </p>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  className="w-full"
                >
                  Scratch Another Card
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

